import { message } from 'antd';
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  io,
  Socket,
} from 'socket.io-client';

import assets from '../assets';
import { store } from '../context/store';
import {
  addOrEditMessage,
} from '../context/thunks/tqConnectServiceRequestThunk';
import {
  setIsServiceRequestSocketConnected,
} from '../context/tqConnectServiceRequestReducer';
import { getDisplayMessageInPlainText } from '../shared/messageUtils';
import { ICommunityMessage } from '../types/communityMessagesTypes';
import { ROUTES } from '../types/routes';
import {
  SERVICE_REQUEST_SOCKET_ACTION_EVENTS_MAP,
  SERVICE_REQUEST_SOCKET_BROADCAST_EVENTS,
  SERVICE_REQUEST_SOCKET_BROADCAST_EVENTS_MAP,
} from '../types/socketTypes';
import {
  IServiceRequestMessage,
} from '../types/tqConnectTypes/service-requests';
import config from './config';
import { NotificationsAPI } from './NotificationsAPI';

const DEFAULT_SOCKET_ERROR_MESSAGE =
  'Sorry, something went wrong. Please reload the page and try again.';

const SERVICE_REQUEST_SOCKET_NAME = 'Service Request Socket';

export class ServiceRequestSocketAPI {
  private static instance: Socket | null = null;

  static isConnectionInitiated = false;

  static isConnected = false;

  static initialize = (refresh?: boolean) => {
    const {
      hostMetadata: { isCommunityFeatureActive, isTQConnectFeatureActive },
    } = store.getState().app;
    const { type: userType } = store.getState().user;

    if (!isCommunityFeatureActive) {
      console.log(
        `❌ Community feature is not active. ${SERVICE_REQUEST_SOCKET_NAME} initialization skipped.`,
      );
      return;
    }

    if (!isTQConnectFeatureActive) {
      console.log(
        `❌ TQ Connect feature is not active. ${SERVICE_REQUEST_SOCKET_NAME} initialization skipped.`,
      );
      return;
    }

    if (userType !== 'fan_completed') {
      console.log(
        `❌ ${SERVICE_REQUEST_SOCKET_NAME} is not available for ${userType} users. Initialization skipped.`,
      );
      return;
    }

    if (this.isConnectionInitiated) {
      console.log(
        `🔄 ${SERVICE_REQUEST_SOCKET_NAME} connection initialization in progress.`,
      );
      return;
    }

    if (this.instance && this.instance.connected && !refresh) {
      console.log(
        `🔌 ${SERVICE_REQUEST_SOCKET_NAME} already initialized and connected.`,
      );
      // Prevent multiple instances
      return this.instance;
    }

    if (refresh) {
      // disconnect original connection if we want to refresh
      this.disconnect();
    }

    const { token } = store.getState().app;

    if (!token) {
      console.error(
        `❌ ${SERVICE_REQUEST_SOCKET_NAME} token not found. Cannot initialize socket.`,
      );
      return null;
    }

    console.log(
      `🔄 Initializing new ${SERVICE_REQUEST_SOCKET_NAME} connection...`,
    );

    console.log({
      socketAuthToken: token,
    });

    this.isConnectionInitiated = true;
    this.instance = io(
      `${config.COMMUNITY_SOCKET_ENDPOINT}${config.COMMUNITY_SOCKET_ENDPOINT?.endsWith('/') ? '' : '/'}service-request-chat` as string,
      {
        transports: ['websocket'],
        auth: { token },
        reconnection: true, // Enable reconnection
        reconnectionAttempts: 10,
        reconnectionDelay: 3000, // Try reconnecting every 3s
        reconnectionDelayMax: 10000,
      },
    );

    this.instance.on('connect', () => {
      this.isConnectionInitiated = false;

      console.log(
        `✅ ${SERVICE_REQUEST_SOCKET_NAME} Connected:`,
        this.instance?.id,
      );

      this.isConnected = true;

      const { dispatch } = store;

      dispatch(setIsServiceRequestSocketConnected(true));

      if (this.instance) {
        this.instance.off(
          SERVICE_REQUEST_SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_CREATED,
        );
        this.instance.on(
          SERVICE_REQUEST_SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_CREATED,
          async (resp) => {
            const newMessage = resp.payload.message as IServiceRequestMessage;

            const displayMessage = getDisplayMessageInPlainText(
              newMessage as unknown as ICommunityMessage,
            );

            if (newMessage?.sender?.name && newMessage?.serviceRequest?.title) {
              NotificationsAPI.send({
                title: `${newMessage?.sender?.name} on ${newMessage?.serviceRequest?.title}`,
                body: displayMessage,
                onClick: () => {
                  window.open(
                    `${window.location.origin}${ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DETAILS.replace(':serviceRequestId', newMessage?.serviceRequest?._id)}`,
                    '_self',
                  );
                },
                soundUrl: assets.audios.notificationSound,
              });
            }

            dispatch(
              addOrEditMessage(newMessage as unknown as IServiceRequestMessage),
            );
          },
        );
      }
    });

    this.instance.on('disconnect', (reason) => {
      console.warn(`⚠️ ${SERVICE_REQUEST_SOCKET_NAME} Disconnected:`, reason);

      this.isConnected = false;

      store.dispatch(setIsServiceRequestSocketConnected(false));

      if (
        reason === 'io server disconnect' ||
        // reason === 'io client disconnect' || Note: commenting, because we should not reconnect when we manually disconnect via client
        reason === 'ping timeout' ||
        reason === 'transport close' ||
        reason === 'transport error'
      ) {
        this.reconnect(); // Handle manual reconnection if disconnected by the server
      }
    });

    this.instance.on('connect_error', (err) => {
      console.error(`🚨 ${SERVICE_REQUEST_SOCKET_NAME} Error:`, err);

      this.isConnected = false;

      store.dispatch(setIsServiceRequestSocketConnected(false));

      if (this.instance) {
        this.instance.io.opts.transports = ['websocket'];
      }
    });

    this.instance?.on('exception', (error: any) => {
      console.error(
        `🚨 ${SERVICE_REQUEST_SOCKET_NAME} Exception Error:`,
        error,
      );

      // If the error indicates authentication failure, logout user
      if (
        error?.status === 'unauthorized' ||
        error?.message === 'JWT verification failed'
      ) {
        console.error('🔒 Unauthorized - Logging Out');

        this.isConnected = false;

        store.dispatch(setIsServiceRequestSocketConnected(false));

        this.instance?.off(); // Remove listeners
        this.instance?.disconnect();
        this.instance = null;

        // show session expired message to the user
        // and reload the page after 2 seconds
        // when the token is expired or invalid and socket gives unauthorized or jwt verification failed error
        message.error('Session expired. Please wait while we reload the page.');

        // TODO: NOTE: If reloading the page does not work for the above case, then we need to re-fetch new access token using refresh token and then re-initialize the socket connection
        // reload the page after 2 seconds
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    });

    return this.instance;
  };

  static getInstance = (): Socket => {
    if (!this.instance) {
      // retry initialization
      console.log(
        `🔄 Retrying ${SERVICE_REQUEST_SOCKET_NAME} initialization...`,
      );

      const newInstance = this.initialize();

      if (!newInstance) {
        throw new Error(
          `❌ ${SERVICE_REQUEST_SOCKET_NAME} instance not initialized. Call initialize first.`,
        );
      }

      return newInstance;
    }

    return this.instance;
  };

  static reconnect() {
    // if (!this.token) {
    //   console.error('❌ No token found, cannot reconnect.');
    //   return;
    // }

    console.log(`🔄 Reconnecting ${SERVICE_REQUEST_SOCKET_NAME}...`);

    this.disconnect(); // Close existing connection before reconnecting
    this.initialize(); // Reinitialize the socket connection
    // this.initialize(this.token);
  }

  static disconnect() {
    if (this.instance) {
      console.log(`🔌 Disconnecting ${SERVICE_REQUEST_SOCKET_NAME}...`);
      this.instance.off();
      this.instance.disconnect();
      this.instance.close();
      this.instance = null;
    }
  }

  static on<T extends keyof SERVICE_REQUEST_SOCKET_BROADCAST_EVENTS_MAP>(
    event: T,
    callback: (data: SERVICE_REQUEST_SOCKET_BROADCAST_EVENTS_MAP[T]) => void,
  ) {
    if (!this.instance || !this.instance.connected) {
      console.error(
        `❌ ${SERVICE_REQUEST_SOCKET_NAME} not connected. Cannot listen to event:`,
        event,
      );
      // message.error(DEFAULT_SOCKET_ERROR_MESSAGE);
      return;
    }

    this.getInstance().on(event, callback as any);
  }

  static emit<T extends keyof SERVICE_REQUEST_SOCKET_ACTION_EVENTS_MAP>(
    event: T,
    data: SERVICE_REQUEST_SOCKET_ACTION_EVENTS_MAP[T],
    callback?: (response: any) => void,
  ) {
    if (!this.instance || !this.instance.connected) {
      console.error(
        `❌ ${SERVICE_REQUEST_SOCKET_NAME} not connected. Cannot emit event:`,
        event,
      );
      // message.error(DEFAULT_SOCKET_ERROR_MESSAGE);
      return;
    }

    if (callback) {
      this.getInstance().emit(event, data, (resp: any) => {
        if (resp.status === 'success') {
          callback(resp);
          return;
        }

        message.error(
          typeof resp?.result === 'string'
            ? resp?.result
            : DEFAULT_SOCKET_ERROR_MESSAGE,
        );
      });
    } else {
      this.getInstance().emit(event, data);
    }
  }
}
